import { Box, Flex, Spinner, TableContainer } from '@chakra-ui/react';
import { useCallback, useMemo, useState } from 'react';

import FieldView from '../field-view';
import { TableFilterDialog } from './components/table-filter';
import { TableHeader } from './components/table-header';
import { TablePagination } from './components/table-pagination';
import { TableBaseProps, TableColumn, TableFilter } from './types';

export function MobileTable<I extends EntityBase = EntityBase>(props: TableBaseProps) {
  const {
    title,
    columns,
    onCreateClick,
    onDownload,
    total,
    keyProperty = 'id',
    initialShowFilter,
    data,
    pagination,
    filter,
    sort,
    onSortChange,
    onFilterChange,
    onPaginationChange,
    loading,
    paper = true,
  } = props;
  const [showFilter, setShowFilter] = useState(initialShowFilter || false);

  const onOpenFilter = useCallback(() => {
    setShowFilter(true);
  }, []);

  const needFilter = useMemo(() => {
    return columns.some((column) => column.options.filter);
  }, [columns]);

  const onPageChange = useCallback((page: number) => {
    onPaginationChange({
      page,
      pageSize: pagination.pageSize,
    });
  }, [onPaginationChange, pagination.pageSize]);

  const onPageSizeChange = useCallback((pageSize: number) => {
    onPaginationChange({
      page: 1,
      pageSize,
    });
  }, [onPaginationChange]);

  const onFilterSubmit = useCallback((filter: TableFilter[]) => {
    onFilterChange(filter);
    setShowFilter(false);
  }, [onFilterChange]);

  const visibleColumns = useMemo(() => columns.filter((column) => !column.options?.hidden), [columns]);

  return <TableContainer 
    backgroundColor='white'
    borderRadius={paper ? 'md' : 'none'}
    boxShadow={paper ? 'md' : 'none'}
    position='relative'
  >
    <TableHeader
      title={title}
      onFilterClick={needFilter ? onOpenFilter : undefined}
      onDownloadClick={onDownload}
      onCreateClick={onCreateClick}
    />
    <Flex 
      direction='column' 
      gap={4} 
    >
      {data?.map((item, index) => <MobileTableItemCard<I>
        key={`${item[keyProperty]}_${index}`}
        columns={visibleColumns} 
        rowIndex={index}      
        item={item}
        totalRows={data.length}
      />)}
    </Flex>
    <TablePagination 
      page={pagination.page || 1}
      pageSize={pagination.pageSize}
      total={total}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
    />
    {loading && <Box 
      position='absolute' backgroundColor='whiteAlpha.500' top={0} left={0} right={0} bottom={0}
      display='flex' alignItems='center' justifyContent='center'
    >
      <Spinner />
    </Box>}
    <TableFilterDialog<I>
      title={title}
      isOpen={showFilter} 
      onClose={() => setShowFilter(false)}
      columns={columns}
      filters={filter}
      onFilterSubmit={onFilterSubmit}
    /> 
  </TableContainer>;
}

function MobileTableItemCard<I>(props: {
  item: I,
  columns: TableColumn<I>[],
  rowIndex: number,
  totalRows: number,
}) {
  const {
    item,
    columns,
    rowIndex,
    totalRows,
  } = props;


  return <Flex 
    gap={4}
    direction='row' 
    flexWrap='wrap'
    paddingX={6}
    paddingY={4}
    justify='space-between'
    borderBottom='1px solid'  
    borderBottomColor='gray.500'
  >
    {columns.map((column, index) => <FieldView 
      key={`${column.name.toString()}_${index}`}
      title={column.label}
      value={column.options?.customBodyRender 
        ? column.options?.customBodyRender(item[column.name], { totalRows, rowIndex, columnIndex: index, rowData: item, column }) 
        : item[column.name]?.toString()}
    />)}
  </Flex>;
}